import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SiteLayout } from '../components/layout/SiteLayout';
import { BlockRenderer } from '../components/blocks/BlockRenderer';
import { PageSeo } from '../components/seo/PageSeo';
import { PageSkeleton } from '../components/skeleton/PageSkeleton';
import { CmsPageContext } from '../contexts/CmsPageContext';
import { useEditMode } from '../contexts/EditModeContext';
import { usePage } from '../hooks/usePage';
import { useHomepageSlug, useSiteLayout } from '../contexts/SiteContext';
import { applyBranding, BRANDING_DEFAULTS } from '../lib/branding';

interface CmsPageProps {
  overrideSlug?: string;
}

const CONTENT_WIDTH_CLASS: Record<string, string> = {
  narrow: 'max-w-2xl',
  normal: 'max-w-5xl',
  wide: 'max-w-7xl',
  full: 'max-w-none',
};

export function CmsPage({ overrideSlug }: CmsPageProps) {
  const { slug: routeSlug } = useParams<{ slug: string }>();
  const homepageSlug = useHomepageSlug();
  const siteLayout = useSiteLayout();
  const { isEditMode, blocks: editModeBlocks, dispatch } = useEditMode();

  // In edit mode the slug comes from ?pageSlug= query param
  const editModeSlug = isEditMode
    ? new URLSearchParams(window.location.search).get('pageSlug') ?? undefined
    : undefined;

  const slug = overrideSlug ?? editModeSlug ?? routeSlug ?? homepageSlug ?? undefined;

  // In edit mode, blocks come from postMessage — skip usePage
  const { data: page, isLoading, error } = usePage(isEditMode ? undefined : slug);

  // Apply per-page theme override — must be declared before any early returns (rules of hooks)
  useEffect(() => {
    if (page?.spaceTheme && Object.keys(page.spaceTheme).length > 0) {
      applyBranding({ ...BRANDING_DEFAULTS, theme: page.spaceTheme as Record<string, string> });
    }
  }, [page]);

  // ── DnD sensors ────────────────────────────────────────────────────────────
  // Use delay instead of distance to prevent browser text selection from
  // interfering with the drag gesture.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { delay: 200, tolerance: 5 } }));

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = editModeBlocks.findIndex((b) => b.id === active.id);
    const newIndex = editModeBlocks.findIndex((b) => b.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(editModeBlocks, oldIndex, newIndex);
    const orderedBlockIds = reordered.map((b) => b.id);
    dispatch({ type: 'REORDER_BLOCKS', orderedBlockIds });
    window.parent.postMessage({ type: 'BLOCK_REORDER', orderedBlockIds }, '*');
  }

  // ── Edit mode rendering ─────────────────────────────────────────────────────
  if (isEditMode) {
    if (editModeBlocks.length === 0) {
      return (
        <SiteLayout fullBleed>
          <div className="flex items-center justify-center py-24 text-zinc-400 text-sm">
            <svg
              className="mr-2 h-4 w-4 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
            </svg>
            Connecting to Visual Builder…
          </div>
        </SiteLayout>
      );
    }

    return (
      <SiteLayout fullBleed>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={editModeBlocks.map((b) => b.id)}
            strategy={verticalListSortingStrategy}
          >
            {/* Extra top/right padding to accommodate block labels and drag handles.
                user-select:none prevents text selection from interfering with DnD. */}
            <div className={`${CONTENT_WIDTH_CLASS[siteLayout.contentWidth ?? 'normal'] ?? 'max-w-5xl'} mx-auto px-4 pr-10 pt-6 select-none`}>
              {editModeBlocks.map((block) => (
                <BlockRenderer key={block.id} block={block} />
              ))}

              {/* "Add block" button at the bottom — opens block catalog in parent */}
              <button
                type="button"
                onClick={() => window.parent.postMessage({ type: 'ADD_BLOCK_ROOT' }, '*')}
                className="w-full mt-6 mb-12 py-6 rounded-xl border-2 border-dashed border-indigo-300 bg-indigo-50/50 hover:bg-indigo-100/70 hover:border-indigo-400 text-indigo-500 hover:text-indigo-600 font-semibold text-sm transition-all flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                Add block
              </button>
            </div>
          </SortableContext>
        </DndContext>
      </SiteLayout>
    );
  }

  // ── Normal (published) rendering ────────────────────────────────────────────

  // No slug: no homepage configured for this space yet
  if (!slug) {
    return (
      <SiteLayout>
        <div className="flex flex-col items-center justify-center py-24 text-center gap-4 px-4">
          <svg className="w-10 h-10 text-zinc-300" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253" />
          </svg>
          <div>
            <h1 className="text-xl font-semibold text-zinc-800">This site is under construction</h1>
            <p className="text-sm text-zinc-500 mt-1">No homepage has been configured yet.</p>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const is404 = error && (error as Error & { status?: number }).status === 404;

  if (isLoading) {
    return (
      <SiteLayout fullBleed>
        <PageSkeleton />
      </SiteLayout>
    );
  }

  if (is404 || (!isLoading && !page)) {
    return (
      <SiteLayout>
        <div className="flex flex-col items-center justify-center py-24 text-center gap-4 px-4">
          <svg className="w-10 h-10 text-zinc-300" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
          <div>
            <h1 className="text-xl font-semibold text-zinc-800">Page not found</h1>
            <p className="text-sm text-zinc-500 mt-1">
              The page you&apos;re looking for doesn&apos;t exist or has been removed.
            </p>
          </div>
        </div>
      </SiteLayout>
    );
  }

  if (!page) return null;

  return (
    <SiteLayout fullBleed>
      <PageSeo
        title={page.metaTitle ?? page.title}
        description={page.metaDescription}
        ogImage={page.ogImageUrl}
      />
      <CmsPageContext.Provider value={{ pageSlug: page.slug }}>
        <div className={`${CONTENT_WIDTH_CLASS[page.spaceLayout?.contentWidth ?? 'normal'] ?? 'max-w-5xl'} mx-auto px-4 py-6 space-y-6`}>
          {page.blocks.map((block) => (
            <BlockRenderer key={block.id} block={block} />
          ))}
          {page.blocks.length === 0 && (
            <div className="text-center py-24 text-zinc-400 text-sm px-4">
              This page has no content yet.
            </div>
          )}
        </div>
      </CmsPageContext.Provider>
    </SiteLayout>
  );
}
